import { ReactElement, ReactNode, useEffect } from "react";
import { BookingState } from "../booking-state/BookingState";
import { useViewProvider } from "../contexts/view-provider-context/ViewProvider";
import { getPageByStep } from "./GetPageByStep";
import { navigationStepsOrder } from "./NavigationStepsOrder";
import { Steps } from "./Steps";
import { useBrandNavigate } from "../navigation/UseBrandNavigate";
import { WizardViewProps } from "./WizardViewProps";
import { useTracking } from "../../tracking";
import { Titles } from "./Tiltes";

function getNextStep(currentStep: Steps): Steps | null {
  const currentStepIndex = navigationStepsOrder.findIndex((step) => step === currentStep);
  const nextStepIndex = currentStepIndex + 1;

  if (nextStepIndex >= navigationStepsOrder.length) {
    return null;
  }

  return navigationStepsOrder[nextStepIndex];
}

function getPreviousStep(currentStep: Steps): Steps | null {
  const currentStepIndex = navigationStepsOrder.findIndex((step) => step === currentStep);
  const previousStepIndex = currentStepIndex - 1;

  if (previousStepIndex < 0) {
    return null;
  }

  return navigationStepsOrder[previousStepIndex];
}

export type WizardNavigate = (navigate: (bookingState: BookingState) => void) => void;

export interface WizardProps {
  currentStep: Steps;
  onNext?: WizardNavigate;
  onPrevious?: WizardNavigate;
  children?: ReactNode;
  disabled: boolean;
}

export function Wizard({ currentStep, onNext, onPrevious, children, disabled, }: WizardProps): ReactElement {
  const viewProvider = useViewProvider();
  const navigate = useBrandNavigate();
  const nextStep = getNextStep(currentStep);
  const previousStep = getPreviousStep(currentStep);
  const trackingService = useTracking();

  useEffect(() => {
    const stepTitle = Titles[currentStep];
    document.title = stepTitle
      ? `Home : Service Booking UAT : ${stepTitle}`
      : 'Home : Service Booking UAT';
  }, [currentStep]);

  const navigateToStep = (step: Steps, bookingState: BookingState): void => {
    const page = getPageByStep(step, bookingState, currentStep);

    if (page) {
      navigate(page);
    }
  };

  const onNextClick = (): void => {
    if (!onNext) return;

    onNext((updatedState: BookingState) => {
      if (currentStep === Steps.ContactDetails) {
        trackingService.trackFormStepButtonClick({
          form: {
            FormStart: false,
            FormName: Titles[currentStep],
          }
        });
      } else {
        if (updatedState.selectedDealer) {
          trackingService.trackStepButtonClick({
            contentId: 'Next',
            partner: {
              informationCountry: 'ZA',
              informationBrand: updatedState.selectedDealer.brand,
              informationName: updatedState.selectedDealer.name,
              informationZIP: updatedState.selectedDealer.postalCode,
              informationBnr: updatedState.selectedDealer.dealerCode,
              SearchNumberOfResults: 0,
            }
          });
        } else {
          trackingService.trackStepButtonClick({
            contentId: 'Next'
          });
        }
      }

      navigateToStep(nextStep!, updatedState);
    });
  };


  const onPreviousClick = (): void => {
    if (!onPrevious) return;

    onPrevious((updatedState: BookingState) => {
      if (currentStep === Steps.ContactDetails) {
        trackingService.trackFormStepButtonClick({
          form: {
            FormStart: false,
            FormName: Titles[currentStep],
          }
        });
      } else {
        trackingService.trackStepButtonClick({
          contentId: 'Previous',
          partner: {
            informationCountry: 'ZA',
            informationBrand: updatedState.selectedDealer?.brand ?? '',
            informationName: updatedState.selectedDealer?.name ?? '',
            informationZIP: updatedState.selectedDealer?.postalCode ?? '',
            informationBnr: updatedState.selectedDealer?.dealerCode ?? '',
            SearchNumberOfResults: 0,
          }
        });
      }

      navigateToStep(previousStep!, updatedState);
    });
  };

  const viewProps: WizardViewProps = {
    currentStep,
    nextStep,
    previousStep,
    children,
    disabled,
    onPreviousClick,
    onNextClick,
  };

  return viewProvider.renderWizard(viewProps);
}
